import { FormikProps } from "formik"
import { CreateUpdateBookingResourceTableSetups } from "../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import {
    MeetingServicePriceListState,
    ProductWithQuantity,
    ResourceItem,
    SelectedPriceListItem,
} from "../../createOrderTypes"
import { IconDetails, PaymentMethodType } from "../../../../../modules/payments/paymentTypes"
import { NullableCalendarEvent } from "../resource/calendar/fullCalendarTypes"
import { MonetaryAmount } from "../../../../../modules/currency/currencyTypes"

export enum DynamicConfigKey {
    PRODUCT_CATALOG = "PRODUCT_CATALOG",
    PRIVATE_MEETING = "PRIVATE_MEETING",
    EXTERNAL_MEETING = "EXTERNAL_MEETING",
    MEETING_NAME = "MEETING_NAME",
    MULTIPLE_DELIVERIES = "MULTIPLE_DELIVERIES",
    NUMBER_OF_SEATS = "NUMBER_OF_SEATS",
    PRODUCT_WISH = "PRODUCT_WISH",
    MERCHANT_ORDER_NOTE = "MERCHANT_ORDER_NOTE",
    SERVICE_CENTER_MESSAGE = "SERVICE_CENTER_MESSAGE",
    SEND_ORDER_COPY = "SEND_ORDER_COPY",
    NUMBER_OF_COVERS = "NUMBER_OF_COVERS",
    MEETING_RESPONSIBLE = "MEETING_RESPONSIBLE",
    MEETING_TIME = "MEETING_TIME",
    RECURRENCY = "RECURRENCY",
    DELIVERY_TYPE = "DELIVERY_TYPE",
    DELIVERY_LOCATIONS = "DELIVERY_LOCATIONS",
    OTHER_DELIVERY_LOCATION = "OTHER_DELIVERY_LOCATION",
    REGISTER_PARTICIPANTS = "REGISTER_PARTICIPANTS",
    AUTO_LOCK_ORDER = "AUTO_LOCK_ORDER",
    PAYMENT_NOTE = "PAYMENT_NOTE",
    BUYER_PARTIES = "BUYER_PARTIES",
    CAN_CREATE_BUYER_PARTY = "CAN_CREATE_BUYER_PARTY",
    CREATE_BUYER_PARTY = "CREATE_BUYER_PARTY",
    MEETING_START_TIME = "MEETING_START_TIME",
    MEETING_END_TIME = "MEETING_END_TIME",
    ADD_PARTICIPANTS_TOGGLE = "ADD_PARTICIPANTS_TOGGLE",
    ADD_PARTICIPANTS_FORM = "ADD_PARTICIPANTS_FORM",
    TABLE_ARANGEMENTS = "TABLE_ARANGEMENTS",
    PAYMENT_METHOD = "PAYMENT_METHOD",
    SATELLITES = "SATELLITES",
}

export type DynamicAccountingKey = `ACCOUNTING_DIMENSION_${number}`
export type ExtendedDynamicConfigKey = DynamicConfigKey | DynamicAccountingKey

export type DynamicMainFormValues = Record<ExtendedDynamicConfigKey | string, any> & CustomFieldsGroup
export interface MeetingServiceTabValues extends DynamicMainFormValues {
    priceList: MeetingServicePriceListState
    initPriceList: SelectedPriceListItem | null
}
export type MainOrderFormikRef = React.RefObject<FormikProps<DynamicMainFormValues>>

export type PaymentMethodOptionValue = PaymentMethodType.INVOICE | PaymentMethodType.CASH
export type PaymentMethodOption = Readonly<{
    value: PaymentMethodOptionValue
    icon?: IconDetails
}>

export type PredefinedPattern = Readonly<{
    pattern?: string
    text?: string
    value?: string | number
}>
export type FieldsParams = Readonly<{
    label: string
    inputField: InputField
}>

export enum MeetingServiceConfigMode {
    LOAD_ON_OPEN = "LOAD_ON_OPEN",
    SEARCH_AS_YOU_TYPE = "SEARCH_AS_YOU_TYPE",
    EAGER_LOAD = "EAGER_LOAD",
}

export enum ValidationType {
    REQUIRED = "REQUIRED",
    IS_TRUE = "IS_TRUE",
    EMAIL = "EMAIL",
    REGEX = "REGEX",
}

type RequiredValidation = Readonly<{
    type: ValidationType.REQUIRED
    message: string
}>

type IsTrueValidation = Readonly<{
    type: ValidationType.IS_TRUE
    message: string
}>

type EmailValidation = Readonly<{
    type: ValidationType.EMAIL
    message: string
}>

type RegexValidation = Readonly<{
    type: ValidationType.REGEX
    regex: string
    message: string
}>

export type ValidationRule = RequiredValidation | IsTrueValidation | EmailValidation | RegexValidation

export type InputField = Readonly<{
    type:
        | "component"
        | "textField"
        | "number"
        | "multiple_select"
        | "dateTimePicker"
        | "select"
        | "label"
        | "checkbox"
        | "button"
        | "toogle"
        | "singleSelect"
        | "autocomplete"
        | "dateTime"
        | string
    mode?: MeetingServiceConfigMode
    isRequired?: boolean
    isMultiline?: boolean
    data?: "text" | "number" | "yyyy-MM-dd hh:mm:ss" | "boolean"
    view?: InputFieldView
    predefined?: PredefinedPattern
    name?: string
    fields?: FieldsParams[]
    rememberLastUsed?: boolean
    maxLength?: number
    maxValue?: number
    maxLenght?: number
    validations?: ValidationRule[]
    paymentMethods?: PaymentMethodOption[]
}>

export enum InputFieldView {
    IMAGE = "IMAGE",
    LIST = "LIST",
}

export type SelectValue = Readonly<{
    label: string
    value: string | number
    isPrefilled?: boolean
}>

export type Rules = Readonly<{
    repeatInDaysForTheFuture?: number
    lockHoursBeforeDelivery?: number
}>

export type DynamicConfigField = Readonly<{
    key: ExtendedDynamicConfigKey
    description: string
    label?: string
    active?: boolean
    inputField?: InputField
    selectValues?: SelectValue[]
    rules?: Rules
}>

export type ConfigSectionGroup = Readonly<{
    title?: string
    fields: DynamicConfigField[]
}>

export type ConfigSection = Readonly<{
    title: string
    groups: ConfigSectionGroup[]
    showInDrawer?: boolean
}>

type DayOfWeekNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 // 1 = Monday ... 7 = Sunday

export type DeliveryTimeDay = Readonly<{
    displayName: string // "Monday" (or localized text)
    dayOfWeek: DayOfWeekNumber
    openingTime: string // "HH:mm"
    closingTime: string // "HH:mm"
}>

export type MeetingServiceConfig = Readonly<{
    sections: ConfigSection[]
    closingDates: string[]
    deliveryTimes?: DeliveryTimeDay[]
}>

type CustomFieldsGroupBase = {
    event: NullableCalendarEvent
    resources: CreateUpdateBookingResourceTableSetups[]
    priceList: MeetingServicePriceListState
    initPriceList: SelectedPriceListItem | null
    fullResourcesList: ResourceItem[]
    initAmount: MonetaryAmount | null
    created?: string
    updated?: string
}

export type CustomFieldsGroup = CustomFieldsGroupBase & {
    [DynamicConfigKey.MULTIPLE_DELIVERIES]?: DeliveryTimesField
}

export type DeliveryWithCart = {
    time: string | null // e.g., "HH:mm"
    orderLines: ProductWithQuantity[]
}

export type DeliveryTimesField = {
    deliveryDate: string | null // e.g., "yyyy-MM-dd"
    deliveryTimes: DeliveryWithCart[]
    activeTime: string | null // e.g., "HH:mm"
}

export enum DeliveryFulfillmentType {
    EATING_IN_CANTEEN = "EATING_IN_CANTEEN",
    DELIVERY = "DELIVERY",
    PICK_UP = "PICK_UP",
}
// BOOKING
export type BookingConfig = Readonly<{
    sections: ConfigSection[]
}>

export type BookingCustomFieldsGroup = {
    startDateTime: string
    endDateTime: string
    resourceIds: string[]
}
